import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { BUILD_TIME } from '../build';
import { LIMIT_FIELDS, type LimitOverrides } from '../rules/limits';
import {
  dateTime, money, moneyExactSigned, pctSigned, plural, shortDate, todayIso,
} from '../lib/format';
import { newId } from '../lib/id';
import { squareDataUrl } from '../lib/image';
import { decimal, decimalOr, isPositiveDecimal } from '../lib/number';
import { convertCash, rateAgainst, rateSentence } from '../state/convertCash';
import { NumberField } from '../components/NumberField';
import { CHECKS, type CustomRule } from '../rules/engine';
import { BENCHMARKS, benchmarksFrom } from '../state/derive';
import { downloadExport, parse, pull, push } from '../lib/sync';
import { savedMessage } from '../lib/exportFile';
import { silencedEntries, unsilence } from '../state/silenced';
import { CeilingsSection } from '../components/CeilingsSection';
import { TransferLog } from '../components/TransferLog';
import { resetBook } from '../data/db';
import { WrongPassphrase } from '../lib/crypto';
import { accountName, idFromName } from '../lib/accounts';
import { effectOf, moveCash, restating, reverse, settle } from '../state/cashMove';
import { ImportTrades } from '../components/ImportTrades';
import { StarterImport } from '../components/StarterImport';
import { BrokerConnection } from '../components/BrokerConnection';
import { ImportCashFlows } from '../components/ImportCashFlows';
import { ImportValues } from '../components/ImportValues';
import { DataGuide } from '../components/DataGuide';
import { HideToggle } from '../components/HideToggle';
import * as api from '../api/client';
import { toUsd } from '../state/derive';
import { useStore } from '../state/store';
import { DEFAULT_THEME, GLOW_RANGE, THEMES } from '../styles/themes';
import type {
  Account, AccountRecord, CashConversion, CashFlow, Currency, FxRates, Snapshot, ThemeRecord,
} from '../types';
import type { Nav } from '../App';

/* Fourteen sections on one page is a scroll, not a settings screen — finding the
   cash balances meant knowing they were somewhere past the themes. They are
   grouped now, and a group opens on its own. The grouping is by what you came
   to do rather than by which part of the code owns it: cash balances and the
   value timeline sit together because both are money you type in, however far
   apart they live in the source. */
interface Group {
  id: string;
  title: string;
  blurb: string;
  /* Drawn here rather than pulled from an icon font, the same way the tab bar
     draws its own: a handful of shapes weigh less than a dependency and they
     inherit the current colour for free. A settings list is scanned rather than
     read, and a shape is what the eye lands on before the words. */
  icon: JSX.Element;
  render: (say: (m: string) => void) => JSX.Element;
}

const stroke = {
  fill: 'none', stroke: 'currentColor', strokeWidth: 1.7,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};

function Glyph({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 20 20" width="19" height="19" aria-hidden="true">{children}</svg>
  );
}

const GROUPS: Group[] = [
  {
    id: 'you',
    icon: (
      // A person: this group is who the book belongs to and how it looks.
      <Glyph>
        <circle cx="10" cy="6.6" r="3.1" {...stroke} />
        <path d="M3.8 17.2c0-3.4 2.8-5.6 6.2-5.6s6.2 2.2 6.2 5.6" {...stroke} />
      </Glyph>
    ),
    
    title: 'You and how it looks',
    blurb: 'What the book is called, its picture, the currency it totals in, and the theme.',
    render: (say) => (
      <>
        <IdentitySection say={say} />
        <CurrencySection />
        <AppearanceSection />
        <ThemesSection say={say} />
      </>
    ),
  },
  {
    id: 'brokers',
    icon: (
      // A columned front — the building the shares are actually held at.
      <Glyph>
        <path d="M2.5 8.2 10 3.4l7.5 4.8" {...stroke} />
        <path d="M5 9.6v6M10 9.6v6M15 9.6v6" {...stroke} />
        <path d="M3 17h14" {...stroke} />
      </Glyph>
    ),
    
    title: 'Brokers',
    blurb: 'The accounts you hold things in, importing their trade files, and the read-only keys that let a broker be read directly.',
    render: (say) => (
      <>
        <BrokersSection say={say} />
        <ConnectionSection say={say} />
      </>
    ),
  },
  {
    id: 'money',
    icon: (
      // Two arrows, one each way: money in and money out.
      <Glyph>
        <path d="M4 7h11" {...stroke} />
        <path d="M12 4l3 3-3 3" {...stroke} />
        <path d="M16 13H5" {...stroke} />
        <path d="M8 10l-3 3 3 3" {...stroke} />
      </Glyph>
    ),
    
    title: 'Money in and out',
    blurb: 'Cash balances, deposits and withdrawals, changing currency, and the values you know from before.',
    render: (say) => (
      <>
        <CashSection say={say} />
        <ConvertSection say={say} />
        <TransferLog say={say} />
        <TimelineSection say={say} />
      </>
    ),
  },
  {
    id: 'limits',
    icon: (
      // A bar with a marker at the ceiling, the same shape the cap bars draw.
      <Glyph>
        <rect x="2.6" y="7.6" width="14.8" height="4.8" rx="2.4" {...stroke} />
        <path d="M13.4 5.6v8.8" {...stroke} />
      </Glyph>
    ),
    
    title: 'Your limits',
    blurb: 'Every threshold the alerts compare against, which of them you want to hear, and any you write yourself.',
    render: (say) => (
      <>
        <LimitsSection say={say} />
        <CeilingsSection say={say} />
        <ChecksSection />
        <CustomRulesSection say={say} />
      </>
    ),
  },
  {
    id: 'telling',
    icon: (
      // A bell: the group that decides when the app speaks up.
      <Glyph>
        <path d="M5.6 14.2V9.4a4.4 4.4 0 0 1 8.8 0v4.8" {...stroke} />
        <path d="M3.8 14.2h12.4" {...stroke} />
        <path d="M8.6 16.6a1.6 1.6 0 0 0 2.8 0" {...stroke} />
      </Glyph>
    ),
    
    title: 'Being told things',
    blurb: 'When a company moves further than you want to hear about, what to compare against, and how a shut exchange is priced.',
    render: (say) => (
      <>
        <MoveAlertsSection say={say} />
        <OffHoursSection />
        <BenchmarkSection />
      </>
    ),
  },
  {
    id: 'filling',
    icon: (
      // An open book with a line going into it: what has to be told to it.
      <Glyph>
        <path d="M10 6.4C8.4 5 6 4.6 3.2 4.9v9.6c2.8-.3 5.2.1 6.8 1.5 1.6-1.4 4-1.8 6.8-1.5V4.9c-2.8-.3-5.2.1-6.8 1.5Z" {...stroke} />
        <path d="M10 6.4v9.6" {...stroke} />
      </Glyph>
    ),

    title: 'Filling a new book',
    blurb: 'The four things the book cannot work out for itself, what each one needs, and a prompt for getting an assistant to prepare the files.',
    render: (say) => <DataGuide say={say} />,
  },
  {
    id: 'data',
    icon: (
      // A drive with an arrow leaving it: the copy that is the real backup.
      <Glyph>
        <rect x="2.8" y="11.4" width="14.4" height="5.6" rx="1.8" {...stroke} />
        <path d="M10 2.8v6.4" {...stroke} />
        <path d="M7.2 6.6 10 9.4l2.8-2.8" {...stroke} />
        <circle cx="14" cy="14.2" r="0.9" fill="currentColor" stroke="none" />
      </Glyph>
    ),
    
    title: 'Backup, sync and starting over',
    blurb: 'The export that is your real backup, the encrypted copy, and loading a book from a file.',
    render: (say) => (
      <>
        <BackupSection say={say} />
        <SyncSection say={say} />
        <StartFromFileSection say={say} />
      </>
    ),
  },
];

export function SettingsScreen({ nav, group }: { nav: Nav; group?: string }) {
  const s = useStore();
  const [toast, setToast] = useState<string | null>(null);
  const say = (m: string) => { setToast(m); window.setTimeout(() => setToast(null), 3600); };

  const open = GROUPS.find((g) => g.id === group);

  return (
    <div className="screen">
      <div className="screen-head">
        <button className="back" onClick={nav.back} aria-label="Back">‹</button>
        <h1>{open ? open.title : 'Settings'}</h1>
        <HideToggle push />
      </div>

      {open ? open.render(say) : (
        <>
          <div className="card card-pad" style={{ padding: 0, overflow: 'hidden' }}>
            {GROUPS.map((g, i) => (
              <button
                key={g.id}
                className="settings-row"
                style={i === GROUPS.length - 1 ? { borderBottom: 'none' } : undefined}
                onClick={() => nav.go({ name: 'settings', group: g.id })}
              >
                <span className="settings-row-icon">{g.icon}</span>
                <span className="settings-row-title">{g.title}</span>
                <span className="settings-row-blurb">{g.blurb}</span>
                <span className="settings-row-chevron" aria-hidden="true">›</span>
              </button>
            ))}
          </div>

          <p className="dim" style={{ fontSize: 11, textAlign: 'center', marginTop: 30, lineHeight: 1.5 }}>
            {s.state.data.holdings.length} lots · {s.state.data.trades.length} trades ·
            {' '}{s.state.data.snapshots.length} measured values
            {/* Which copy this is. An update that seems to have changed nothing
                is first a question of whether it is running at all. */}
            {BUILD_TIME ? <><br />Build {dateTime(BUILD_TIME)}</> : null}
          </p>
        </>
      )}

      {toast ? <div className="toast">{toast}</div> : null}
    </div>
  );
}

/* ---------------- appearance ---------------- */

function AppearanceSection() {
  const { state, saveSettings } = useStore();
  const current = state.settings.theme ?? DEFAULT_THEME;
  const glow = state.settings.glowStrength ?? 1;
  const hideAmounts = state.settings.hideAmounts === true;
  const showLogos = state.settings.showLogos !== false;

  return (
    <section>
      <div className="section-head"><h2>Appearance</h2></div>
      <div className="card card-pad">
        <div className="theme-grid">
          {THEMES.map((t) => (
            <button
              key={t.id}
              className="theme-card"
              aria-pressed={current === t.id}
              onClick={() => void saveSettings({ theme: t.id })}
            >
              <span className="theme-swatches" aria-hidden="true">
                {t.swatches.map((c, i) => (
                  <i key={i} style={{ background: c }} />
                ))}
              </span>
              <span className="theme-name">
                {t.name}
                {current === t.id ? <span className="theme-tick" aria-hidden="true">✓</span> : null}
              </span>
              <span className="theme-blurb">{t.blurb}</span>
            </button>
          ))}
        </div>

        <div className="hint" style={{ marginTop: 14 }}>
          A theme changes the surfaces, the accent and the up/down colours. It leaves the twelve
          position colours alone, since those are picked to be told apart from each other, and it never
          makes colour the only signal: every rise and fall keeps its arrow, every breach its words.
        </div>

        <div className="divider" />

        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="glow-strength">
            Background glow · {Math.round(glow * 100)}%
          </label>
          <input
            id="glow-strength"
            className="slider"
            type="range"
            min={GLOW_RANGE.min}
            max={GLOW_RANGE.max}
            step={GLOW_RANGE.step}
            value={glow}
            style={{ '--fill': `${(glow / GLOW_RANGE.max) * 100}%` } as CSSProperties}
            onChange={(e) => void saveSettings({ glowStrength: Number(e.target.value) })}
          />
          <div className="control-row" style={{ marginTop: 8 }}>
            <button className="btn btn-sm" onClick={() => void saveSettings({ glowStrength: 0 })}>
              Off
            </button>
            <button className="btn btn-sm" onClick={() => void saveSettings({ glowStrength: 1 })}>
              As designed
            </button>
            <button className="btn btn-sm" onClick={() => void saveSettings({ glowStrength: 2 })}>
              Full
            </button>
          </div>
          <div className="hint">
            How far the two soft washes behind the page come up. It moves only how strong they are.
            the colours and where they sit are the theme's, and nothing that carries meaning is
            touched. The headers at the top of each screen carry the same wash, so it reads as one
            background rather than stopping at a band.
          </div>
        </div>

        <div className="divider" />

        <div className="field">
          <label htmlFor="show-logos">Company logos</label>
          <div className="control-row" style={{ marginTop: 2 }}>
            <button
              id="show-logos"
              className={showLogos ? 'btn btn-primary btn-block' : 'btn btn-block'}
              aria-pressed={showLogos}
              onClick={() => void saveSettings({ showLogos: !showLogos })}
            >
              {showLogos ? 'Logos are on' : 'Monograms only'}
            </button>
          </div>
          <div className="hint">
            Each position's own mark on its row instead of two letters of its ticker. Fetched once
            per name and kept as bytes, so it draws with no network afterwards, and a name the
            service has no logo for keeps its monogram rather than an empty square.
            {' '}
            <strong>The request goes through the Worker, never from this device</strong>. Asking a
            third party about bitcoin.org and zetaglobal.com from your phone would hand it the list
            of what you hold. Off, nothing is asked at all.
            {' '}
            Logos by <a href="https://www.allinvestview.com/tools/ticker-logos/"
                        target="_blank" rel="noreferrer">AllInvestView</a>.
          </div>
        </div>

        <div className="divider" />

        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="hide-amounts">Hide amounts</label>
          <div className="control-row" style={{ marginTop: 2 }}>
            <button
              id="hide-amounts"
              className={hideAmounts ? 'btn btn-primary btn-block' : 'btn btn-block'}
              aria-pressed={hideAmounts}
              onClick={() => void saveSettings({ hideAmounts: !hideAmounts })}
            >
              {hideAmounts ? 'Amounts are hidden' : 'Amounts are showing'}
            </button>
          </div>
          <div className="hint">
            Every figure in money, meaning the total, each position, cash, contracts and realised results, and
            every share count prints as dots instead. Weights, prices, dates and everything else
            carry on, so the book still reads as a book: you can see that one name is 39% and which
            way it moved, without showing what that is worth. There is a switch for this in the
            header of the main screens too, because the reason to use it usually walks up to you.
            It stays on this device. It is not part of the book, so it does not travel on a sync.
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- currency ---------------- */

function CurrencySection() {
  const { state, saveSettings } = useStore();
  return (
    <section>
      <div className="section-head"><h2>Display</h2></div>
      <div className="card card-pad">
        <div className="field">
          <label>Currency</label>
          <div className="seg">
            {(['USD', 'PLN'] as const).map((c) => (
              <button key={c} aria-pressed={state.settings.displayCurrency === c}
                      onClick={() => void saveSettings({ displayCurrency: c })}>{c}</button>
            ))}
          </div>
          <div className="hint">
            The book totals in dollars either way. Choosing złoty converts at the current NBP rate.
          </div>
        </div>

        <div className="field" style={{ marginBottom: 0 }}>
          <label>Cycle reading</label>
          <div className="seg">
            <button aria-pressed={!state.settings.cycleIsHot}
                    onClick={() => void saveSettings({ cycleIsHot: false })}>Normal</button>
            <button aria-pressed={state.settings.cycleIsHot}
                    onClick={() => void saveSettings({ cycleIsHot: true })}>Hot</button>
          </div>
          <div className="hint">Marking the cycle hot raises the cash floor.</div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- brokers ---------------- */

const CURRENCIES: Currency[] = ['USD', 'PLN', 'EUR', 'HKD'];

function BrokersSection({ say }: { say: (m: string) => void }) {
  const { state, putAccount, removeAccount } = useStore();
  const accounts = state.data.accounts;
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<Currency>('USD');
  const [shielded, setShielded] = useState(false);

  /* An id is stamped on every holding, trade and cash flow, so a broker that
     still holds anything cannot simply disappear — the records would point at
     nothing. Cash balances and open contracts count too: a futures account can
     hold no shares at all and still be carrying the whole leveraged book, which
     is exactly how MEXC looks here. */
  const usedBy = (id: Account) => {
    const record = accounts.find((a) => a.id === id);
    return (
      state.data.holdings.filter((h) => h.account === id).length +
      state.data.trades.filter((t) => t.account === id).length +
      state.data.cashflows.filter((f) => f.account === id).length +
      state.data.cash.filter((c) => c.account === id && c.amount !== 0).length +
      (record?.isFutures ? state.data.perps.length : 0)
    );
  };

  const add = () => {
    const label = name.trim();
    if (!label) return say('Give it a name.');
    const id = idFromName(label);
    if (accounts.some((a) => a.id === id)) return say('You already have a broker by that name.');
    const record: AccountRecord = {
      id, name: label, currency,
      order: accounts.reduce((m, a) => Math.max(m, a.order), -1) + 1,
    };
    if (shielded) record.taxShielded = true;
    void putAccount(record);
    setName(''); setShielded(false);
    say(`${label} added.`);
  };

  return (
    <section>
      <div className="section-head"><h2>Brokers</h2></div>
      <div className="card card-pad">
        {accounts.map((a) => {
          const depends = usedBy(a.id);
          return (
            <div key={a.id} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  className="input" value={a.name} aria-label={`Name for ${a.name}`}
                  onChange={(e) => void putAccount({ ...a, name: e.target.value })}
                />
                <select
                  className="select" style={{ flex: '0 0 88px' }} value={a.currency}
                  aria-label={`Currency for ${a.name}`}
                  onChange={(e) => void putAccount({ ...a, currency: e.target.value as Currency })}
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 7 }}>
                <button
                  className="tag" data-kind={a.taxShielded ? 'ok' : 'muted'}
                  aria-pressed={a.taxShielded === true} style={{ cursor: 'pointer', padding: '4px 8px' }}
                  onClick={() => void putAccount({ ...a, taxShielded: !a.taxShielded })}
                >
                  {a.taxShielded ? '✓ ' : ''}TAX SHIELDED
                </button>
                <span className="dim" style={{ fontSize: 11, marginLeft: 'auto' }}>
                  {depends === 0 ? 'nothing recorded here' : `${depends} records`}
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={depends > 0}
                  title={depends > 0 ? 'Move or close what is here first' : 'Remove this broker'}
                  onClick={() => { void removeAccount(a.id); say(`${a.name} removed.`); }}
                >
                  Remove
                </button>
              </div>
              <ImportTrades account={a} say={say} />
            </div>
          );
        })}

        <div className="divider" />

        <div className="field">
          <label htmlFor="broker-name">Add a broker</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input id="broker-name" className="input" value={name} placeholder="Where the account is"
                   onChange={(e) => setName(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
            <select className="select" style={{ flex: '0 0 88px' }} value={currency}
                    aria-label="Currency" onChange={(e) => setCurrency(e.target.value as Currency)}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
                          textTransform: 'none', letterSpacing: 0, fontSize: 13, color: 'var(--dim2)' }}>
            <input type="checkbox" checked={shielded} onChange={(e) => setShielded(e.target.checked)} />
            Gains here are shielded from tax
          </label>
          <button className="btn btn-block" style={{ marginTop: 12 }} onClick={add}>Add it</button>
        </div>
      </div>
    </section>
  );
}

/* ---------------- sync ---------------- */

function SyncSection({ say }: { say: (m: string) => void }) {
  const { state, replaceBook, saveSettings } = useStore();
  const [passphrase, setPassphrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);

  const lastSyncedAt = state.settings.lastSyncedAt ?? null;
  const token = state.settings.blobToken;

  const doPush = async (force: boolean) => {
    if (!passphrase) return say('Enter your passphrase first.');
    setBusy(true);
    try {
      const result = await push(state.data, passphrase, lastSyncedAt,
        { force, ...(token ? { token } : {}) });
      if (result.status === 'conflict') {
        setConflict(result.theirsAt);
      } else {
        await saveSettings({ lastSyncedAt: result.updatedAt });
        setConflict(null);
        say('Saved to the other device.');
      }
    } catch (e) {
      say(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const doPull = async () => {
    if (!passphrase) return say('Enter your passphrase first.');
    setBusy(true);
    try {
      const result = await pull(passphrase, token);
      if (!result) return say('Nothing has been saved yet.');
      await replaceBook(result.book);
      await saveSettings({ lastSyncedAt: result.updatedAt ?? undefined });
      say('Pulled the saved copy.');
    } catch (e) {
      say(e instanceof WrongPassphrase
        ? 'That passphrase does not open this copy. Nothing was changed.'
        : e instanceof Error ? e.message : 'Could not read the saved copy.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="section-head"><h2>Sync</h2></div>
      <div className="card card-pad">
        <div className="field">
          <label htmlFor="pass">Passphrase</label>
          <input id="pass" className="input" type="password" value={passphrase}
                 autoComplete="off" placeholder="Never leaves this device"
                 onChange={(e) => setPassphrase(e.target.value)} />
          <div className="hint">
            The book is encrypted here before it is sent. The server stores a blob it cannot read.
          </div>
        </div>

        <div className="fab-row" style={{ marginTop: 0 }}>
          <button className="btn btn-block" disabled={busy} onClick={() => void doPull()}>
            Pull
          </button>
          <button className="btn btn-primary btn-block" disabled={busy} onClick={() => void doPush(false)}>
            Push
          </button>
        </div>

        <details style={{ marginTop: 14 }}>
          <summary className="dim" style={{ fontSize: 12, cursor: 'pointer' }}>
            Server token
          </summary>
          <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
            <input
              className="input" type="password" autoComplete="off"
              aria-label="Server token"
              placeholder="Only if the Worker has one set"
              value={state.settings.blobToken ?? ''}
              onChange={(e) => void saveSettings({ blobToken: e.target.value || undefined })}
            />
            <div className="hint">
              This only decides who may reach the storage endpoint. It is not what protects your
              book. The passphrase above is, and it never leaves this device.
            </div>
          </div>
        </details>

        <div className="dim" style={{ fontSize: 11, marginTop: 10 }}>
          {lastSyncedAt ? `Last synced ${dateTime(lastSyncedAt)}` : 'Not synced from this device yet.'}
        </div>
      </div>

      {conflict ? (
        <div className="modal-scrim" role="dialog" aria-modal="true" aria-labelledby="conflict-title">
          <div className="modal">
            <h2 id="conflict-title">The other device saved changes</h2>
            <p>
              The saved copy was written {dateTime(conflict)}, after the one this device started
              from. Pushing now replaces it with what is on this screen, and there is no merge.
            </p>
            <div className="modal-actions">
              <button className="btn" onClick={() => setConflict(null)}>Cancel</button>
              <button className="btn btn-danger" disabled={busy}
                      onClick={() => void doPush(true)}>Overwrite them</button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/* ---------------- backup ---------------- */

/* ---------------- what the book is called ---------------- */

/** Kept small on purpose. A phone photograph is several megabytes and this
    database is the only copy of every thesis in the book, so the picture is
    drawn down to a square thumbnail before it is stored. */
const AVATAR_PX = 128;

/* ---------------- being told something moved ---------------- */

const THRESHOLDS = [0, 3, 5, 8, 10] as const;

/* ---------------- the line drawn against the book ---------------- */

function BenchmarkSection() {
  const { state, saveSettings } = useStore();
  const chosen = benchmarksFrom(state.settings);

  const toggle = (id: string) => {
    const next = chosen.includes(id) ? chosen.filter((c) => c !== id) : [...chosen, id];
    void saveSettings({ benchmarks: next });
  };

  return (
    <section>
      <div className="section-head">
        <h2>Compare against</h2>
        <span className="meta">{chosen.length === 0 ? 'none' : `${chosen.length} drawn`}</span>
      </div>
      <div className="card card-pad">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {BENCHMARKS.map((b) => {
            const on = chosen.includes(b.id);
            return (
              <button key={b.id} className="tag" data-kind={on ? 'ok' : 'muted'}
                      aria-pressed={on} style={{ cursor: 'pointer', padding: '6px 10px' }}
                      onClick={() => toggle(b.id)}>
                {on ? '✓ ' : ''}{b.label}
              </button>
            );
          })}
        </div>
        <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          Each is drawn on the value chart as its own dashed line, started from wherever your book
          started in the window on screen, so the gap between the lines is the difference,
          readable without arithmetic. Pick more than one: whether this beat the index has a
          different answer for each index, and seeing only the flattering one is how a book
          congratulates itself. They appear in the Value view only; against &ldquo;gains
          only&rdquo; or &ldquo;return %&rdquo; they would be comparing different quantities.
        </div>
      </div>
    </section>
  );
}

/* Pricing a shut exchange from the contract MEXC lists on the same company.
   Default on, because a book that stops moving for sixteen hours a day is the
   complaint that produced it — and a switch, because it changes what the
   headline number means and that should be the owner's call. */
function OffHoursSection() {
  const { state, book, saveSettings } = useStore();
  const on = state.settings.offHoursMarks !== false;
  const priced = book.positions.filter((p) => p.offHoursFrom !== null);

  return (
    <section>
      <div className="section-head">
        <h2>While the exchange is shut</h2>
        <span className="meta">{on ? 'on' : 'off'}</span>
      </div>
      <div className="card card-pad">
        <div className="kv" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13 }}>Price from the MEXC contract</div>
            <div className="dim" style={{ fontSize: 11, lineHeight: 1.45, marginTop: 2 }}>
              MEXC lists perpetual contracts on around 290 individual companies and they trade
              around the clock.
            </div>
          </div>
          <button className="tag" data-kind={on ? 'ok' : 'muted'} aria-pressed={on}
                  style={{ cursor: 'pointer', padding: '5px 9px', flex: '0 0 auto' }}
                  onClick={() => void saveSettings({ offHoursMarks: !on })}>
            {on ? 'ON' : 'OFF'}
          </button>
        </div>

        <div className="dim" style={{ fontSize: 11, lineHeight: 1.5, marginTop: 10 }}>
          Used only while the listing itself is quoting nothing. Any session, including pre and
          post, outranks it. The figure taken is the contract&rsquo;s <strong>index</strong>, which
          is the exchange&rsquo;s answer to what the share is worth, not the price the contract
          changes hands at. And it never reaches the value chart: history is written from
          regular-hours closes, the same rule that keeps a thin pre-market print out of it.
          {priced.length > 0
            ? ` Right now ${priced.map((p) => p.ticker).join(', ')} ${priced.length === 1 ? 'is' : 'are'} being priced this way.`
            : ' No company held has a contract quoting right now.'}
        </div>
      </div>
    </section>
  );
}

function MoveAlertsSection({ say }: { say: (m: string) => void }) {
  const { state, book, saveSettings } = useStore();
  const pct = state.settings.moveAlertPct ?? 0;
  const muted = state.settings.mutedMovers ?? [];

  const followed = [
    ...book.positions.map((p) => ({ symbol: p.yahooSymbol, label: p.ticker, owned: true })),
    ...state.data.watchlist.map((w) => ({ symbol: w.yahooSymbol, label: w.ticker, owned: false })),
  ];

  const toggle = (symbol: string) => {
    const next = muted.includes(symbol)
      ? muted.filter((s) => s !== symbol)
      : [...muted, symbol];
    void saveSettings({ mutedMovers: next });
  };

  const ask = async () => {
    if (typeof Notification === 'undefined') return say('This device has no notifications.');
    if (Notification.permission === 'granted') return say('Already allowed.');
    const result = await Notification.requestPermission();
    say(result === 'granted' ? 'Allowed.' : 'Not allowed. The strip on the book still shows them.');
  };

  return (
    <section>
      <div className="section-head"><h2>Tell me when something moves</h2></div>
      <div className="card card-pad">
        <div className="seg" style={{ marginBottom: 10 }}>
          {THRESHOLDS.map((t) => (
            <button key={t} aria-pressed={pct === t} onClick={() => void saveSettings({ moveAlertPct: t })}>
              {t === 0 ? 'Off' : `${t}%`}
            </button>
          ))}
        </div>

        <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          A company moving further than this in a day shows on the book, and, if you allow it,
          as a notification. <strong>Only while the app is open.</strong> §3.4 says never to refresh
          in the background, so there is no moment when this is shut and still knows a price moved.
          Sending them to a closed phone would need a server watching your positions for you, which
          is the one thing §1 rules out.
        </div>

        {pct > 0 ? (
          <>
            <button className="btn btn-block" style={{ marginTop: 12 }} onClick={() => void ask()}>
              Allow notifications
            </button>

            <div className="divider" />
            <div className="dim" style={{ fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>
              Quiet ones
            </div>
            <div className="hscroll">
              {followed.map((f) => (
                <button key={f.symbol} className="btn btn-sm"
                        aria-pressed={muted.includes(f.symbol)}
                        onClick={() => toggle(f.symbol)}>
                  {muted.includes(f.symbol) ? '🔇 ' : ''}{f.label}
                </button>
              ))}
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
              Tap one to stop hearing about it. Useful for a name you already know is jumpy.
            </div>
          </>
        ) : null}

        <div className="divider" />

        <div className="dim" style={{ fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>
          Reports and ex-dates
        </div>
        <div className="seg">
          {[0, 1, 3, 7].map((days) => (
            <button key={days} aria-pressed={(state.settings.remindDaysAhead ?? 0) === days}
                    onClick={() => void saveSettings({ remindDaysAhead: days })}>
              {days === 0 ? 'Off' : `${days} day${days === 1 ? '' : 's'} ahead`}
            </button>
          ))}
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}>
          A report coming up on a position over your plan threshold says so. The day to decide what
          a miss and a beat each mean is before the report, not after it. An ex-date is the one that
          decides whether a dividend is yours.
        </div>
      </div>
    </section>
  );
}

function IdentitySection({ say }: { say: (m: string) => void }) {
  const { state, saveSettings } = useStore();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const avatar = state.settings.avatar;

  return (
    <section>
      <div className="section-head"><h2>Name and picture</h2></div>
      <div className="card card-pad">
        <div className="field">
          <label htmlFor="app-name">What this book is called</label>
          <input
            id="app-name" className="input" placeholder="Position Book"
            value={state.settings.appName ?? ''}
            onChange={(e) => void saveSettings({ appName: e.target.value })}
          />
          <div className="hint">Shown in the corner. Leave it empty for the original.</div>
        </div>

        <input
          ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            void squareDataUrl(file, { px: AVATAR_PX, mime: 'image/jpeg' })
              .then((data) => saveSettings({ avatar: data }))
              .then(() => say('Picture set.'))
              .catch(() => say('That file could not be read as a picture.'));
          }}
        />

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className="avatar" data-photo={avatar ? 'true' : undefined} aria-hidden="true">
            {avatar ? <img src={avatar} alt="" /> : (state.settings.appName ?? 'PB').slice(0, 2).toUpperCase()}
          </span>
          <button className="btn btn-sm" onClick={() => fileRef.current?.click()}>
            {avatar ? 'Change the picture' : 'Choose a picture'}
          </button>
          {avatar ? (
            <button className="btn btn-ghost btn-sm"
                    onClick={() => void saveSettings({ avatar: '' }).then(() => say('Picture removed.'))}>
              Remove
            </button>
          ) : null}
        </div>

        <div className="dim" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.45 }}>
          The picture never leaves this device. It is stored with the book and travels only inside
          the encrypted sync blob, like everything else here.
        </div>
      </div>
    </section>
  );
}

function ConnectionSection({ say }: { say: (m: string) => void }) {
  return (
    <section>
      <div className="section-head">
        <h2>Broker connection</h2>
        <span className="meta">read-only keys, kept on this device</span>
      </div>
      <div className="card card-pad">
        <BrokerConnection say={say} />
      </div>
    </section>
  );
}

function StartFromFileSection({ say }: { say: (m: string) => void }) {
  return (
    <section>
      <div className="section-head"><h2>Start from a file</h2></div>
      <div className="card card-pad">
        <StarterImport say={say} />
      </div>
    </section>
  );
}

function BackupSection({ say }: { say: (m: string) => void }) {
  const { state, replaceBook, saveSettings, reload } = useStore();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  /* The starting book is loaded once, on a device's first run. When it is
     corrected afterwards — a contract reopened, a cost basis finally found — an
     already-seeded device keeps the old copy and has no way back to it. */
  const resetToSeed = async () => {
    await resetBook();
    await reload();
    setConfirmReset(false);
    say('Back to the starting book.');
  };

  const exportNow = async () => {
    try {
      say(savedMessage(await downloadExport(state.data)));
    } catch (e) {
      /* The shell's own words. A refusal to write a file is a sentence worth
         reading, and inventing a friendlier one loses the reason. */
      say(e instanceof Error ? e.message
        : typeof e === 'string' ? e : 'The file could not be written.');
    }
  };

  const onFile = async (file: File) => {
    try {
      const book = parse(await file.text());
      await replaceBook(book);
      say('Imported.');
    } catch (e) {
      say(e instanceof Error ? e.message : 'That file could not be read.');
    }
  };

  return (
    <section>
      <div className="section-head"><h2>Backup</h2></div>
      <div className="card card-pad">
        {/* Said once, plainly, at setup (§6) */}
        {!state.settings.backupAcknowledged ? (
          <div className="note" data-tone="warn" style={{ marginBottom: 14 }}>
            <strong>Read this once.</strong> If you lose the passphrase, the synced copy cannot be
            recovered by anyone, including you. This export is the real backup. Keep a recent one
            somewhere you can actually find it.
            <div style={{ marginTop: 10 }}>
              <button className="btn btn-sm"
                      onClick={() => void saveSettings({ backupAcknowledged: true })}>
                Understood
              </button>
            </div>
          </div>
        ) : null}

        <div className="fab-row" style={{ marginTop: 0 }}>
          <button className="btn btn-block" onClick={() => fileRef.current?.click()}>Import JSON</button>
          {/* Says what happened, in both directions. It used to say nothing at
              all, which in the macOS shell meant the only backup the app tells
              you to keep failed without a word. */}
          <button className="btn btn-primary btn-block" onClick={() => void exportNow()}>
            Export to file
          </button>
        </div>
        <input ref={fileRef} type="file" accept="application/json,.json" hidden
               onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = ''; }} />
        <div className="hint" style={{ marginTop: 10 }}>
          The export is plain readable JSON. Importing replaces everything in this book.
        </div>

        <StorageState />

        <div className="divider" />

        <button className="btn btn-danger btn-block" onClick={() => setConfirmReset(true)}>
          Start over from the seeded book
        </button>
        <div className="hint" style={{ marginTop: 8 }}>
          Throws away everything on this device and reloads the starting positions. Use it when the
          starting data has been corrected since you first opened the app.
        </div>
      </div>

      {confirmReset ? (
        <div className="modal-scrim" role="dialog" aria-modal="true" aria-labelledby="reset-title">
          <div className="modal">
            <h2 id="reset-title">Throw away this device's book?</h2>
            <p>
              Every position, thesis, trade and measured value on this device goes, and the starting
              book takes their place. Nothing on your other device or in the synced copy is touched.
              Export first if you are not certain.
            </p>
            <div className="modal-actions">
              <button className="btn" onClick={() => setConfirmReset(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => void resetToSeed()}>Start over</button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** Whether the browser has agreed not to evict this book under storage
    pressure. Installing the app usually flips it; it is worth being able to see
    which side of that line you are on. */
function StorageState() {
  const [durable, setDurable] = useState<boolean | null>(null);
  useEffect(() => {
    void navigator.storage?.persisted?.().then(setDurable).catch(() => setDurable(null));
  }, []);
  if (durable === null) return null;
  return (
    <div className="note" data-tone={durable ? undefined : 'warn'} style={{ marginTop: 12 }}>
      {durable
        ? 'This browser has marked the book as durable. It will not be cleared to reclaim space.'
        : 'This browser has not marked the book as durable yet, so it could be cleared if the device runs out of space. Installing the app to your home screen or dock usually settles it. Keep an export either way.'}
    </div>
  );
}

/* ---------------- limits ---------------- */

function LimitsSection({ say }: { say: (m: string) => void }) {
  const { state, limits, saveSettings } = useStore();
  const overrides = state.settings.limitOverrides ?? {};

  const setLimit = (key: keyof Omit<typeof limits, 'positionCaps'>, n: number, text: string) => {
    const next: LimitOverrides = { ...overrides };
    // An emptied field is not a limit of zero — it puts the limit back to the
    // one the spec sets.
    if (text.trim() === '' || !Number.isFinite(n)) delete next[key];
    else next[key] = n;
    void saveSettings({ limitOverrides: next });
  };

  return (
    <section>
      <div className="section-head">
        <h2>Your limits</h2>
        <button className="btn btn-ghost btn-sm"
                onClick={() => { void saveSettings({ limitOverrides: {} }); say('Back to the defaults.'); }}>
          Reset
        </button>
      </div>
      <div className="card card-pad">
        <div className="dim" style={{ fontSize: 11.5, marginBottom: 14, lineHeight: 1.5 }}>
          These are your own rules, not the app's. Changing one here changes every check that uses it.
        </div>

        {LIMIT_FIELDS.map((f) => (
          <div className="field" key={f.key}>
            <label htmlFor={`lim-${f.key}`}>{f.label}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <NumberField id={`lim-${f.key}`} value={limits[f.key]}
                           onChange={(n, text) => setLimit(f.key, n, text)} />
              <span className="dim" style={{ fontSize: 12, flex: '0 0 auto' }}>{f.unit}</span>
            </div>
          </div>
        ))}

        {/* The position ceilings were here as nine fixed rows. They are records
            now, edited in their own section below — which also means this
            card's Reset genuinely only touches the numbers above it. */}
      </div>
    </section>
  );
}

/* ---------------- which alerts you want to hear ---------------- */

/* The checks are the user's own limits, so which of them are worth hearing is
   theirs too. A rule you have decided not to run is a different thing from a
   rule you are quietly breaking — and an alert list nobody reads because four
   of its entries are noise is worse than a shorter list that gets read.

   Switched off, not deleted: the wording stays, so turning one back on brings
   back the sentence it always had rather than something reconstructed. */
function ChecksSection() {
  const { state, saveSettings } = useStore();
  const off = state.settings.checksOff ?? [];

  const toggle = (kind: string) => {
    const next = off.includes(kind) ? off.filter((k) => k !== kind) : [...off, kind];
    void saveSettings({ checksOff: next });
  };

  return (
    <section>
      <div className="section-head">
        <h2>Which alerts you want</h2>
        <span className="meta">{off.length === 0 ? 'all on' : `${off.length} off`}</span>
      </div>
      <div className="card card-pad">
        {CHECKS.map((c) => {
          const on = !off.includes(c.kind);
          return (
            <div className="kv" key={c.kind} style={{ alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: on ? 'var(--text)' : 'var(--dim)' }}>{c.label}</div>
                <div className="dim" style={{ fontSize: 11, lineHeight: 1.45, marginTop: 2 }}>{c.why}</div>
              </div>
              <button className="tag" data-kind={on ? 'ok' : 'muted'} aria-pressed={on}
                      style={{ cursor: 'pointer', padding: '5px 9px', flex: '0 0 auto' }}
                      onClick={() => toggle(c.kind)}>
                {on ? 'ON' : 'OFF'}
              </button>
            </div>
          );
        })}
        <div className="dim" style={{ fontSize: 11, lineHeight: 1.45, marginTop: 4 }}>
          Switching one off stops it being shown. It does not stop being true. The figure it
          watches is still on the position&rsquo;s own screen.
        </div>
      </div>

      <SilencedSection />
    </section>
  );
}

/* Checks switched off on one position rather than on the book.
 *
 * They are set from the position's own screen, which is where the judgement
 * gets made, and they have to be undoable somewhere that does not require
 * remembering which holding it was. A rule you silenced eight months ago and
 * cannot find is not a setting, it is a thing wrong with the app. So: the whole
 * list, each row saying what it silences and where, each with its own undo. */
function SilencedSection() {
  const { state, saveSettings } = useStore();
  const entries = silencedEntries(state.settings.silencedChecks);
  if (entries.length === 0) return null;

  const label = (kind: string) =>
    CHECKS.find((c) => c.kind === kind)?.label ?? kind;

  return (
    <div className="card card-pad" style={{ marginTop: 10 }}>
      <div className="fin-title">Silenced on one position</div>
      {entries.map((e) => (
        <div className="kv" key={`${e.ticker}:${e.kind}`}
             style={{ alignItems: 'flex-start', marginTop: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13 }}>
              <span className="num">{e.ticker}</span> · {label(e.kind)}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ flex: '0 0 auto' }}
                  onClick={() => void saveSettings({
                    silencedChecks: unsilence(state.settings.silencedChecks, e.ticker, e.kind),
                  })}>
            Undo
          </button>
        </div>
      ))}
      <div className="dim" style={{ fontSize: 11, lineHeight: 1.45, marginTop: 10 }}>
        These were switched off from a position&rsquo;s own screen. Everything else about those
        positions is still reported, and the figures behind these have not changed.
      </div>
    </div>
  );
}

/* ---------------- limits of your own ---------------- */

const METRICS: Array<{ id: CustomRule['metric']; label: string; unit: string; book: boolean }> = [
  { id: 'weight', label: 'weight', unit: '%', book: false },
  { id: 'price', label: 'price', unit: '', book: false },
  { id: 'return', label: 'return from cost', unit: '%', book: true },
  { id: 'value', label: 'value', unit: '', book: true },
];

function CustomRulesSection({ say }: { say: (m: string) => void }) {
  const { state, book, saveSettings } = useStore();
  const rules = state.settings.customRules ?? [];

  const [ticker, setTicker] = useState('');
  const [metric, setMetric] = useState<CustomRule['metric']>('weight');
  const [direction, setDirection] = useState<'above' | 'below'>('above');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [severity, setSeverity] = useState<'warning' | 'critical'>('warning');

  const usable = METRICS.filter((m) => (ticker === '' ? m.book : true));
  const add = () => {
    if (!isPositiveDecimal(value) && decimal(value) !== 0) {
      return say('Give it a number to compare against.');
    }
    const rule: CustomRule = {
      id: newId('rule'), ticker: ticker.trim().toUpperCase(), metric, direction,
      value: decimal(value), severity,
    };
    if (note.trim()) rule.note = note.trim();
    void saveSettings({ customRules: [...rules, rule] });
    setValue('');
    setNote('');
    say('Added.');
  };

  const remove = (id: string) =>
    void saveSettings({ customRules: rules.filter((r) => r.id !== id) });

  const describe = (r: CustomRule) => {
    const what = METRICS.find((m) => m.id === r.metric)?.label ?? r.metric;
    const unit = METRICS.find((m) => m.id === r.metric)?.unit ?? '';
    return `${r.ticker || 'The book'} ${what} ${r.direction} ${r.value}${unit}`;
  };

  return (
    <section>
      <div className="section-head">
        <h2>Limits of your own</h2>
        <span className="meta">{rules.length === 0 ? 'none' : plural(rules.length, 'rule')}</span>
      </div>
      <div className="card card-pad">
        {rules.length > 0 ? (
          <div style={{ marginBottom: 14 }}>
            {rules.map((r) => (
              <div className="kv" key={r.id} style={{ marginBottom: 8 }}>
                <span className="k num" style={{ fontSize: 12 }}>{describe(r)}</span>
                {r.severity === 'critical' ? (
                  <span className="tag" data-kind="crit">BREACH</span>
                ) : null}
                <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                        aria-label={`Remove ${describe(r)}`} onClick={() => remove(r.id)}>×</button>
              </div>
            ))}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8 }}>
          <div className="field" style={{ flex: 1, minWidth: 0 }}>
            <label htmlFor="cr-ticker">Company</label>
            <select id="cr-ticker" className="select" value={ticker}
                    onChange={(e) => {
                      const next = e.target.value;
                      setTicker(next);
                      // Weight and price mean nothing about a whole book.
                      if (next === '' && (metric === 'weight' || metric === 'price')) setMetric('value');
                    }}>
              <option value="">The whole book</option>
              {book.positions.map((p) => <option key={p.ticker} value={p.ticker}>{p.ticker}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 0 }}>
            <label htmlFor="cr-metric">Watch its</label>
            <select id="cr-metric" className="select" value={metric}
                    onChange={(e) => setMetric(e.target.value as CustomRule['metric'])}>
              {usable.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div className="field" style={{ flex: 1, minWidth: 0 }}>
            <label htmlFor="cr-dir">Tell me when it goes</label>
            <select id="cr-dir" className="select" value={direction}
                    onChange={(e) => setDirection(e.target.value as 'above' | 'below')}>
              <option value="above">above</option>
              <option value="below">below</option>
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 0 }}>
            <label htmlFor="cr-value">This figure</label>
            <input id="cr-value" className="input" data-mono="true" inputMode="decimal"
                   value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label htmlFor="cr-note">What to say when it does</label>
          <input id="cr-note" className="input" placeholder="optional · your own words"
                 value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="cr-sev">How loudly</label>
          <select id="cr-sev" className="select" value={severity}
                  onChange={(e) => setSeverity(e.target.value as 'warning' | 'critical')}>
            <option value="warning">Watch · sits with the rest</option>
            <option value="critical">Breach · counted in the header</option>
          </select>
        </div>

        <button className="btn btn-block" onClick={add}>Add this limit</button>

        <div className="dim" style={{ fontSize: 11, lineHeight: 1.45, marginTop: 10 }}>
          One shape on purpose: a figure this book already shows, a direction, and a number. Every
          built-in check is of that form too, so nothing here makes a claim the rest of the book
          would not. A rule about a company you no longer hold, or a price that never arrived,
          reports nothing rather than reporting zero.
        </div>
      </div>
    </section>
  );
}

/* ---------------- themes ---------------- */

/* A theme's name, which was the one label in the book that could only be
   deleted and retyped.
 *
 * Deleting took its membership with it: a theme is a name and a list of
 * tickers, and rebuilding "Critical minerals and energy" after a typo meant
 * ticking eleven companies again. `setThemes` already handles a rename — it
 * drops any stored theme missing from the list it is given and writes the rest,
 * so the record moves and its tickers ride along. Only the door was missing.
 *
 * Saved on a button rather than on each keystroke, unlike the broker names
 * above: the theme's name is its key in the store, so live editing would
 * create and destroy a record per character typed. */
function ThemeName({ theme, themes, setThemes, say }: {
  theme: ThemeRecord;
  themes: ThemeRecord[];
  setThemes: (next: ThemeRecord[]) => Promise<void>;
  say: (m: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(theme.name);

  const save = () => {
    const next = name.trim();
    if (next === '' || next === theme.name) return setEditing(false);
    if (themes.some((t) => t.name.toLowerCase() === next.toLowerCase())) {
      return say('You already have a theme by that name.');
    }
    void setThemes(themes.map((t) => (t.name === theme.name ? { ...t, name: next } : t)));
    setEditing(false);
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <input className="input" style={{ flex: 1, minWidth: 0 }} value={name}
               aria-label={`Rename ${theme.name}`} autoFocus
               onChange={(e) => setName(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
        <button className="btn btn-sm" onClick={() => { setName(theme.name); setEditing(false); }}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" onClick={save}>Save</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 500 }}>{theme.name}</span>
      <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
              onClick={() => { setName(theme.name); setEditing(true); }}>
        Rename
      </button>
      <button className="btn btn-ghost btn-sm"
              onClick={() => void setThemes(themes.filter((t) => t.name !== theme.name))}>
        Remove
      </button>
    </div>
  );
}


function ThemesSection({ say }: { say: (m: string) => void }) {
  const { state, book, setThemes } = useStore();
  const themes = state.data.themes;
  const [newName, setNewName] = useState('');

  const toggle = (theme: ThemeRecord, ticker: string) => {
    const has = theme.tickers.includes(ticker);
    const next = themes.map((t) => t.name !== theme.name ? t : {
      ...t,
      tickers: has ? t.tickers.filter((x) => x !== ticker) : [...t.tickers, ticker],
    });
    void setThemes(next);
  };

  return (
    <section>
      <div className="section-head"><h2>Themes</h2></div>
      <div className="card card-pad">
        <div className="field">
          <label htmlFor="newtheme">Add a theme</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input id="newtheme" className="input" value={newName}
                   placeholder="What is the one bet?" onChange={(e) => setNewName(e.target.value)} />
            <button className="btn" disabled={!newName.trim()} onClick={() => {
              const name = newName.trim();
              if (themes.some((t) => t.name.toLowerCase() === name.toLowerCase())) {
                return say('You already have a theme by that name.');
              }
              void setThemes([...themes, { name, tickers: [] }]);
              setNewName('');
            }}>Add</button>
          </div>
        </div>

        {themes.map((theme) => (
          <div key={theme.name} style={{ marginBottom: 18 }}>
            <ThemeName theme={theme} themes={themes} setThemes={setThemes} say={say} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {book.positions.map((p) => {
                const on = theme.tickers.includes(p.ticker);
                return (
                  <button key={p.ticker} className="tag" data-kind={on ? 'ok' : 'muted'}
                          aria-pressed={on} style={{ cursor: 'pointer', padding: '4px 8px' }}
                          onClick={() => toggle(theme, p.ticker)}>
                    {on ? '✓ ' : ''}{p.ticker}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- cash and cash flows ---------------- */

function CashSection({ say }: { say: (m: string) => void }) {
  const { state, book, setCash, putCashFlow, removeRecord } = useStore();
  const accounts = state.data.accounts;
  /* Empty means the deposit is not about one broker. It is the default because
     the figure most people come here to type is the capital the book started
     with, which arrived across several accounts over years nobody wrote down —
     and naming one of them would be filing a real number under a detail that
     was made up to satisfy the form. */
  const [account, setAccount] = useState<Account | ''>('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [at, setAt] = useState(todayIso());
  /* The entry being corrected, if any. The same form does both, so a deposit
     typed with a digit missing is fixed where it was entered rather than
     deleted and retyped from memory. */
  const [editingId, setEditingId] = useState<string | null>(null);



  const startEdit = (f: CashFlow) => {
    setEditingId(f.id);
    setAccount(f.account ?? '');
    setAmount(String(f.amount));
    setCurrency(f.currency);
    setAt(f.at.slice(0, 10));
  };

  const stopEdit = () => {
    setEditingId(null);
    setAmount('');
    setAccount('');
    setCurrency('USD');
    setAt(todayIso());
  };

  const addFlow = async () => {
    const value = decimal(amount);
    if (!Number.isFinite(value) || value === 0) return say('Enter an amount.');
    const flow: CashFlow = {
      id: editingId ?? newId('flow'),
      amount: value, currency, at: new Date(`${at}T12:00:00Z`).toISOString(),
    };
    if (account) flow.account = account;

    /* §5.1 converts each flow at the rate on its own date, and a deposit is
       normally written down long after it landed. NBP keeps its old tables, so
       that rate is not lost — it just has to be asked for by date. Taking
       today's instead moved every historical deposit by whatever the złoty had
       done since, which is exactly the kind of quiet error the money-paid-in
       line cannot afford. */
    if (currency === 'USD') flow.rateToUsd = 1;
    else {
      let table: FxRates | undefined;
      try { table = await api.fetchFxOn(at); } catch { table = state.fx; }
      const rate = toUsd(1, currency, table);
      if (rate !== null) flow.rateToUsd = rate;
    }

    await putCashFlow(flow);

    /* Money paid in moves the account's free cash, because that is what paying
       money in does — and correcting it re-states that movement rather than
       repeating it: whatever this entry did before is undone, and what it says
       now is applied. Both land in one settlement, so a correction that stays
       on the same account nets instead of overwriting itself. */
    const wasEdit = editingId !== null;
    const previous = wasEdit ? state.data.cashflows.find((f) => f.id === flow.id) : undefined;
    const wanted = effectOf(moveCash(book.cashByAccount, accounts, account, value, currency, state.fx));
    /* On a correction the record keeps whatever standing it had: one that never
       moved a balance goes on not moving one, so fixing a typo is never the
       moment money leaves. */
    const effect = wasEdit && !previous?.cashEffect ? undefined : wanted;
    if (effect) flow.cashEffect = effect;

    await putCashFlow(flow);
    const moves = wasEdit
      ? settle(book.cashByAccount, restating(previous?.cashEffect, effect))
      : settle(book.cashByAccount, [effect]);
    for (const m of moves) await setCash(m.account, m.amount, m.currency);

    stopEdit();
    /* What the balance actually did just now, which on a correction is the
       difference and not the whole entry. Reporting the entry's own effect
       would say "moved by 150" when it moved by fifty. */
    const moved = moves.find((m) => m.account === account);
    const balanceNote = !account
      ? ''
      : moved
        ? ` ${accountName(accounts, account)}’s free cash moved by ${moneyExactSigned(moved.applied, moved.currency as 'USD')}.`
        : wasEdit && !previous?.cashEffect
          ? ' This entry predates the book moving balances, so it never touched one and correcting it does not either. Delete it and enter it again if it should.'
          : wanted
            ? ''
            : ' The balance was left alone. There was no rate to cross those currencies with, and one guessed here would be written into a figure you are the authority on.';
    say(currency !== 'USD' && flow.rateToUsd === undefined
      ? `Saved, but no rate was available for that date, so it will convert at the current one.${balanceNote}`
      : `${wasEdit ? 'Corrected.' : 'Recorded.'}${balanceNote}`);
  };

  return (
    <section>
      <div className="section-head"><h2>Cash</h2></div>
      <div className="card card-pad">
        <div className="dim" style={{ fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>
          Balances
        </div>
        {accounts.map((acc) => {
          const c = book.cashByAccount.find((x) => x.account === acc.id);
          return (
            <div className="field" key={acc.id}>
              <label htmlFor={`cash-${acc.id}`}>{acc.name} ({c?.currency ?? acc.currency})</label>
              <NumberField id={`cash-${acc.id}`} value={c?.amount ?? 0}
                           onChange={(n) => void setCash(acc.id, Number.isFinite(n) ? n : 0, c?.currency ?? acc.currency)} />
            </div>
          );
        })}

        <div className="divider" />
        <div className="dim" style={{ fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>
          Starting capital, deposits and withdrawals
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.5 }}>
          Start with the money you began with: one entry, dated when you began. Deposits and
          withdrawals are what separate what the market added from what you paid in.
          Without them the book cannot tell the difference.
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="flow-amount">Amount</label>
            <input id="flow-amount" className="input" data-mono="true" inputMode="decimal"
                   placeholder="+ in, − out" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="field" style={{ flex: '0 0 92px' }}>
            <label htmlFor="flow-ccy">Currency</label>
            <select id="flow-ccy" className="select" value={currency}
                    onChange={(e) => setCurrency(e.target.value)}>
              <option value="USD">USD</option>
              <option value="PLN">PLN</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Wider than the date beside it: the widest choice in it is a phrase
              rather than a broker's name, and a clipped "Across the boo" is not
              a label anyone can act on. */}
          <div className="field" style={{ flex: '1.4 1 0', minWidth: 0 }}>
            <label htmlFor="flow-account">Account</label>
            <select id="flow-account" className="select" value={account}
                    onChange={(e) => setAccount(e.target.value as Account | '')}>
              <option value="">Across the book</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: '1 1 0', minWidth: 0 }}>
            <label htmlFor="flow-date">Date</label>
            <input id="flow-date" className="input" type="date" value={at}
                   onChange={(e) => setAt(e.target.value)} />
          </div>
        </div>
        <div className="fab-row" style={{ marginTop: 0 }}>
          {editingId ? (
            <button className="btn btn-block" onClick={stopEdit}>Cancel</button>
          ) : null}
          <button className={`btn btn-block${editingId ? ' btn-primary' : ''}`}
                  onClick={() => void addFlow()}>
            {editingId ? 'Save the correction' : 'Record it'}
          </button>
        </div>

        <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
          The account is optional. Money paid in counts against the whole book, and nothing here
          asks which broker it went to, so a total that arrived across several of them, or one
          you no longer have the split for, goes in as <em>across the book</em>. Naming a broker
          is worth doing when you know it and worth leaving alone when you would be guessing.
        </div>

        <div className="divider" />
        <div className="dim" style={{ fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>
          Bring a history in
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.5 }}>
          Years of deposits are the one thing in this book nobody types twice. Paste them, or load
          the file your broker gave you, and check the totals against your own before it writes
          anything.
        </div>
        <ImportCashFlows say={say} />

        {state.data.cashflows.length > 0 ? (
          <div style={{ marginTop: 16 }}>
            {[...state.data.cashflows].sort((a, b) => b.at.localeCompare(a.at)).map((f) => (
              <div className="kv" key={f.id} style={{ marginBottom: 8 }}>
                <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', minHeight: 0 }}
                        aria-label={`Correct the entry of ${shortDate(f.at)}`}
                        onClick={() => startEdit(f)}>
                  <span className="num">{shortDate(f.at)}</span>
                </button>
                <span className={f.account ? 'k' : 'k dim'}>{accountName(accounts, f.account)}</span>
                <span className="v num" style={{ marginLeft: 'auto' }}>
                  {f.amount > 0 ? '+' : ''}{f.amount.toLocaleString('en-US')} {f.currency}
                </span>
                {/* Deleting takes back exactly what recording it put in. Leaving
                    the balance where it was is how this looked plainly broken:
                    money that could be added and never taken away. */}
                <button className="btn btn-ghost btn-sm"
                        onClick={() => void (async () => {
                          await removeRecord('cashflows', f.id);
                          for (const m of settle(book.cashByAccount, [reverse(f.cashEffect)])) {
                            await setCash(m.account, m.amount, m.currency);
                          }
                        })()} aria-label="Remove">×</button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ---------------- rebuilding the line ---------------- */

function RebuildHistory({ say }: { say: (m: string) => void }) {
  const { state, rebuildHistory, clearRebuiltHistory } = useStore();
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState('auto');
  const rebuiltCount = state.data.snapshots.filter((s) => s.reconstructed).length;

  const run = async () => {
    setBusy(true);
    try {
      const r = await rebuildHistory(range === 'auto' ? undefined : range);
      if (r.added === 0) say('No price history came back.');
      else {
        say(`${r.added} days rebuilt${r.from ? `, back to ${r.from}` : ''}.`);
        if (r.carriedDays > 0) {
          say(`${r.carriedDays} of them had a name that did not trade that day; `
            + 'it kept its previous close rather than dropping out.');
        }
        if (r.missing.length > 0) {
          say(`No history came back for ${r.missing.join(', ')}, so those are held flat `
            + 'at what they are worth today on every past point.');
        }
      }
    } catch {
      say('Could not reach the price service.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="dim" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.5 }}>
        The chart can also be filled backwards from real closing prices. Be clear about what that
        gives you: <strong>what today's holdings would have been worth</strong> on each past day.
        not what the account was worth. They differ by whatever you bought and sold in between, and
        cash and the leveraged book are held at today's figures because their open dates are not in
        the record. Days you actually recorded are kept and drawn as dots. A name that did not trade
        on a given day keeps its last real close rather than dropping the day from the line.
      </div>

      <div className="seg" style={{ marginBottom: 10 }}>
        {(['auto', '3mo', '6mo', '1y'] as const).map((r) => (
          <button key={r} aria-pressed={range === r} onClick={() => setRange(r)}>
            {r === 'auto' ? 'since you began' : r}
          </button>
        ))}
      </div>

      <div className="fab-row" style={{ marginTop: 0 }}>
        {rebuiltCount > 0 ? (
          <button className="btn btn-block" disabled={busy} onClick={() => void (async () => {
            const n = await clearRebuiltHistory();
            say(`${n} rebuilt days removed.`);
          })()}>
            Remove rebuilt
          </button>
        ) : null}
        <button className="btn btn-primary btn-block" disabled={busy} onClick={() => void run()}>
          {busy ? 'Reading prices…' : rebuiltCount > 0 ? 'Rebuild again' : 'Fill from closing prices'}
        </button>
      </div>

      {rebuiltCount > 0 ? (
        <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
          {rebuiltCount} rebuilt days on the chart, alongside{' '}
          {state.data.snapshots.length - rebuiltCount} you recorded.
        </div>
      ) : null}
    </>
  );
}

/* ---------------- value timeline ---------------- */

/* Changing cash from one currency into another.
 *
 * XTB keeps a złoty account and a dollar one, and moving between them settles
 * at whatever rate the broker gave that morning — not the National Bank's, and
 * not any figure this app holds. The only way to record it before was to type
 * over two balances, which left no trace of why either changed.
 *
 * It is emphatically not a deposit. Converting 1,000 złoty into 250 dollars
 * adds nothing to the book, so it writes no cash flow and the money-paid-in
 * figure does not move — that separation is what makes every return figure in
 * the book mean anything. */
function ConvertSection({ say }: { say: (m: string) => void }) {
  const { state, book, convertCash: save, removeConversion } = useStore();
  const accounts = state.data.accounts;
  const balanceOf = (id: string) => book.cashByAccount.find((c) => c.account === id) ?? null;

  const [fromAccount, setFromAccount] = useState<Account>(accounts[0]?.id ?? '');
  const [toAccount, setToAccount] = useState<Account>(accounts[1]?.id ?? accounts[0]?.id ?? '');
  const [gave, setGave] = useState('');
  const [got, setGot] = useState('');
  const [toCurrency, setToCurrency] = useState('USD');
  const [at, setAt] = useState(todayIso());
  const [reference, setReference] = useState<number | null>(null);

  const fromBalance = balanceOf(fromAccount);
  const fromCurrency = fromBalance?.currency
    ?? accounts.find((a) => a.id === fromAccount)?.currency ?? 'USD';

  const draft = {
    fromAccount, fromAmount: decimalOr(gave), fromCurrency,
    toAccount, toAmount: decimalOr(got), toCurrency,
  };
  const preview = convertCash(book.cashByAccount, draft);

  /* What the National Bank published that day, so the rate you were actually
     given can be read against something. Fetched on demand rather than on every
     keystroke, and a failure leaves the comparison simply absent. */
  const compare = async () => {
    try {
      const table = await api.fetchFxOn(at);
      const perPln = (code: string) =>
        code === 'PLN' ? 1
          : code === 'USD' ? table.usdPln
          : code === 'EUR' ? table.eurPln
          : code === 'HKD' ? table.hkdPln
          : undefined;
      const f = perPln(fromCurrency);
      const t = perPln(toCurrency);
      // Units of `to` per unit of `from`, through złoty.
      setReference(f && t ? f / t : null);
    } catch {
      setReference(null);
      say('No published rate for that date, so the comparison is left out.');
    }
  };

  const commit = async () => {
    if (!preview) return say('Give both amounts, and two sides that differ.');
    const record: CashConversion = {
      id: newId('fxc'),
      at: new Date(`${at}T12:00:00Z`).toISOString(),
      fromAccount, fromAmount: draft.fromAmount, fromCurrency,
      toAccount, toAmount: draft.toAmount, toCurrency,
    };
    await save(record);
    setGave('');
    setGot('');
    setReference(null);
    say(`Recorded at ${rateSentence(record)}.`);
  };

  const against = preview ? rateAgainst(draft, reference) : null;

  return (
    <section>
      <div className="section-head">
        <h2>Changing currency</h2>
        <span className="meta">not a deposit</span>
      </div>
      <div className="card card-pad">
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="field" style={{ flex: 1, minWidth: 0 }}>
            <label htmlFor="fx-from">Out of</label>
            <select id="fx-from" className="select" value={fromAccount}
                    onChange={(e) => setFromAccount(e.target.value)}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({balanceOf(a.id)?.currency ?? a.currency})
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 0 }}>
            <label htmlFor="fx-gave">Gave ({fromCurrency})</label>
            <input id="fx-gave" className="input" data-mono="true" inputMode="decimal"
                   value={gave} onChange={(e) => setGave(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div className="field" style={{ flex: 1, minWidth: 0 }}>
            <label htmlFor="fx-to">Into</label>
            <select id="fx-to" className="select" value={toAccount}
                    onChange={(e) => setToAccount(e.target.value)}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: '0 0 92px' }}>
            <label htmlFor="fx-ccy">As</label>
            <select id="fx-ccy" className="select" value={toCurrency}
                    onChange={(e) => setToCurrency(e.target.value)}>
              <option value="USD">USD</option>
              <option value="PLN">PLN</option>
              <option value="EUR">EUR</option>
              <option value="HKD">HKD</option>
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 0 }}>
            <label htmlFor="fx-got">Got</label>
            <input id="fx-got" className="input" data-mono="true" inputMode="decimal"
                   value={got} onChange={(e) => setGot(e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label htmlFor="fx-date">When</label>
          <input id="fx-date" className="input" type="date" value={at}
                 onChange={(e) => { setAt(e.target.value); setReference(null); }} />
        </div>

        {preview ? (
          <>
            <div className="kv">
              <span className="k">Rate you were given</span>
              <span className="v num">{rateSentence(draft)}</span>
            </div>
            <div className="kv">
              <span className="k">{accountName(accounts, fromAccount)} after</span>
              <span className={`v num ${preview.overdrawn ? 'crit' : ''}`}>
                {preview.fromAfter.toLocaleString('en-US', { maximumFractionDigits: 2 })} {fromCurrency}
              </span>
            </div>
            <div className="kv">
              <span className="k">{accountName(accounts, toAccount)} after</span>
              <span className="v num">
                {preview.toAfter.toLocaleString('en-US', { maximumFractionDigits: 2 })} {toCurrency}
              </span>
            </div>
            {against !== null ? (
              <div className="kv">
                <span className="k">Against the published rate</span>
                <span className={`v num ${against >= 0 ? 'up' : 'down'}`}>{pctSigned(against)}</span>
              </div>
            ) : null}
            {preview.overdrawn ? (
              <div className="note" data-tone="warn" style={{ marginTop: 8 }}>
                That is more than the balance holds. Recorded anyway, since a balance can be out
                of date, but check it before relying on the figure.
              </div>
            ) : null}
          </>
        ) : null}

        <div className="fab-row" style={{ marginTop: 10 }}>
          <button className="btn btn-block" disabled={!preview} onClick={() => void compare()}>
            Compare the rate
          </button>
          <button className="btn btn-primary btn-block" disabled={!preview}
                  onClick={() => void commit()}>
            Record it
          </button>
        </div>

        <div className="dim" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.45 }}>
          This moves money between balances and records the rate. It is <strong>not</strong> a
          deposit: the same money is in a different currency, so money paid in does not change and
          neither does your return. What does move, slightly and truthfully, is the book&rsquo;s
          total. A conversion done at a worse rate than the book values at leaves less than it
          started with.
        </div>

        {state.data.conversions.length > 0 ? (
          <>
            <div className="divider" />
            {state.data.conversions.map((c) => (
              <div className="kv" key={c.id} style={{ marginBottom: 8 }}>
                <span className="k num" style={{ fontSize: 11.5 }}>{shortDate(c.at)}</span>
                <span className="dim" style={{ fontSize: 11 }}>
                  {c.fromAmount.toLocaleString('en-US', { maximumFractionDigits: 2 })} {c.fromCurrency}
                  {' → '}
                  {c.toAmount.toLocaleString('en-US', { maximumFractionDigits: 2 })} {c.toCurrency}
                </span>
                <span className="v num" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                  {rateSentence(c)}
                </span>
                <button className="btn btn-ghost btn-sm"
                        aria-label={`Remove the conversion of ${shortDate(c.at)}`}
                        onClick={() => void removeConversion(c.id)}>×</button>
              </div>
            ))}
            <div className="dim" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.45 }}>
              Removing one takes the record out and leaves the balances where they are. Undoing the
              movement too would assume nothing has happened to either account since.
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}

function TimelineSection({ say }: { say: (m: string) => void }) {
  const { state, putSnapshot, removeRecord } = useStore();
  const [date, setDate] = useState(todayIso());
  const [value, setValue] = useState('');

  const add = () => {
    const total = decimal(value);
    if (!Number.isFinite(total) || total <= 0) return say('Enter the value on that date.');
    const snap: Snapshot = {
      at: new Date(`${date}T12:00:00Z`).toISOString(),
      totalUsd: total,
      // A hand-entered point carries no contributed figure of its own; the chart
      // shows the value line for it and leaves the gains line alone.
      contributedUsd: 0,
      seeded: true,
    };
    void putSnapshot(snap);
    setValue('');
    say('Added to the timeline.');
  };

  // Measurements first: a few hundred rebuilt rows would bury them.
  const rows = [...state.data.snapshots].sort((a, b) => {
    if (!!a.reconstructed !== !!b.reconstructed) return a.reconstructed ? 1 : -1;
    return b.at.localeCompare(a.at);
  });

  return (
    <section>
      <div className="section-head"><h2>Value timeline</h2></div>
      <div className="card card-pad">
        <div className="dim" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.5 }}>
          The book records one value per hour while it is open, and there is no way to get history
          back from a broker. Anything you know from before, type in here.
        </div>

        <RebuildHistory say={say} />

        <div className="divider" />
        <div className="dim" style={{ fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 8 }}>
          Bring a past in
        </div>
        <ImportValues say={say} />

        <div className="divider" />

        <div style={{ display: 'flex', gap: 8 }}>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="snap-date">Date</label>
            <input id="snap-date" className="input" type="date" value={date}
                   onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="snap-value">Value in USD</label>
            <input id="snap-value" className="input" data-mono="true" inputMode="decimal"
                   value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-block" onClick={add}>Add the point</button>

        <div style={{ marginTop: 16 }}>
          {rows.slice(0, 40).map((snap) => (
            <div className="kv" key={snap.at} style={{ marginBottom: 8 }}>
              <span className="k num">{shortDate(snap.at)}</span>
              {snap.reconstructed ? <span className="tag" data-kind="muted">REBUILT</span>
                : snap.seeded ? <span className="tag" data-kind="muted">TYPED IN</span> : null}
              <span className="v num" style={{ marginLeft: 'auto' }}>{money(snap.totalUsd)}</span>
              <button className="btn btn-ghost btn-sm"
                      onClick={() => void removeRecord('snapshots', snap.at)} aria-label="Remove">×</button>
            </div>
          ))}
          {rows.length > 40 ? (
            <div className="dim" style={{ fontSize: 11 }}>…and {rows.length - 40} more.</div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
